import { createApi } from '@reduxjs/toolkit/query/react';
import { RecordResponse } from '@/models/ServiceTypes';
import { normalizePath } from '@/utils/appMenuFilter';
import { waServicesQuery } from './baseQueries';

export interface MenuItemPermission {
  RoleName: string;
  Enabled: boolean;
  Editable?: boolean;
  IsParentEnabled?: boolean;
  AdvisorGroupId?: number | null;
  AdvisorGroupName?: string | null;
}

export interface AdvisorGroupMenuItemRecord {
  AdvisorGroupId?: number;
  Label?: string;
  MenuItemRole?: string;
  RoleMenuId?: number;
  Order?: number;
  IsParent?: boolean;
  IsOrderingDisabled?: boolean;
  DefaultOrder?: number;
  ParentId?: number | null;
  Level?: number;
  CanRename?: boolean;
  DefaultGroup?: string | null;
  DefaultView?: string | null;
  Url?: string | null;
  HasPageConfig?: boolean;
  Permissions: MenuItemPermission[] | null | undefined;
  Children: AdvisorGroupMenuItemRecord[] | null | undefined;
}

export type AdvisorGroupPageVisibilityMap = Record<string, boolean>;

// Widened locally: a real backend can send `{"Records": null}` despite
// RecordResponse<T> typing Records as non-optional.
type AdvisorGroupMenuItemResponse = Omit<
  RecordResponse<AdvisorGroupMenuItemRecord>,
  'Records'
> & {
  Records: AdvisorGroupMenuItemRecord[] | null | undefined;
};

function flattenInvestorVisibility(
  records: AdvisorGroupMenuItemRecord[],
): AdvisorGroupPageVisibilityMap {
  const map: AdvisorGroupPageVisibilityMap = {};

  const visit = (nodes: AdvisorGroupMenuItemRecord[]): void => {
    for (const node of nodes) {
      if (node.Url) {
        const investorPermission = (node.Permissions ?? []).find(
          (permission) => permission.RoleName === 'Investor',
        );
        if (investorPermission) {
          // Legacy "Client Can See" ANDs Enabled with IsParentEnabled — a
          // disabled parent must hide the child even if Enabled is true.
          map[normalizePath(node.Url)] =
            investorPermission.Enabled &&
            Boolean(investorPermission.IsParentEnabled);
        }
      }
      const children = node.Children ?? [];
      if (children.length > 0) {
        visit(children);
      }
    }
  };

  visit(records);
  return map;
}

export const pageVisibilityApi = createApi({
  reducerPath: 'pageVisibilityApi',
  baseQuery: waServicesQuery,
  tagTypes: ['AdvisorGroupPageVisibility'],
  endpoints: (builder) => ({
    getAdvisorGroupPageVisibility: builder.query<
      AdvisorGroupPageVisibilityMap,
      number
    >({
      query: (advisorGroupId) => ({
        url: `/api/firm/advisorGroups/${String(advisorGroupId)}/MenuItems`,
        method: 'GET',
        params: { role: 'Investor' },
      }),
      transformResponse: (
        response: AdvisorGroupMenuItemResponse,
      ): AdvisorGroupPageVisibilityMap =>
        flattenInvestorVisibility(response.Records ?? []),
      providesTags: (_result, _error, advisorGroupId) => [
        { type: 'AdvisorGroupPageVisibility', id: advisorGroupId },
      ],
    }),
  }),
});

export const { useGetAdvisorGroupPageVisibilityQuery } = pageVisibilityApi;
